// src/lib/utils/url.ts
// ─────────────────────────────────────────────────────────────────────────────
// Generic helpers
// ─────────────────────────────────────────────────────────────────────────────

// Validate URL format
export function isValidUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Extract every URL from a text blob—whether it’s already wrapped in an <a> tag
 * or just pasted as naked text (http(s)://, www.…, or bare domain like youtu.be).
 * Normalizes all matches to have an https:// scheme and de-duplicates.
 */
export function extractUrls(text: string): string[] {
  // 1) Match http(s):// or www.… links
  // 2) Match bare domains (e.g. youtu.be, example.com) by looking for word chars + dot + TLD
  const URL_PATTERN =
    /\b((?:https?:\/\/|www\.)[^\s"'<>]+|[a-z0-9-]+(?:\.[a-z0-9-]+)+[^\s"'<>]*)/gi;

  const matches = text.match(URL_PATTERN) || [];
  const normalized = matches.map((u) => {
    // already has scheme?
    if (/^https?:\/\//i.test(u)) return u;
    // starts with www. or bare domain
    return `https://${u}`;
  });

  // Also pull out any fully-qualified URLs inside href="…"
  const hrefRegex = /href=["'](https?:\/\/[^"'<>&]+)["']/gi;
  const hrefMatches: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = hrefRegex.exec(text)) !== null) {
    hrefMatches.push(m[1]);
  }

  return Array.from(new Set([...normalized, ...hrefMatches]));
}

// Detect common image extensions
export function isImageUrl(url: string): boolean {
  const exts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
  return exts.some((ext) => url.toLowerCase().endsWith(ext));
}

// Detect common video extensions
export function isVideoUrl(url: string): boolean {
  const exts = ['.mp4', '.webm', '.ogg', '.mov', '.m4v'];
  return exts.some((ext) => url.toLowerCase().endsWith(ext));
}

/* ────────────────────────────────────────────────────────────────────────────
   YouTube helpers
   Covers:
     • https://www.youtube.com/watch?v=VIDEOID&feature=share
     • https://youtube.com/shorts/VIDEOID?si=xxxx
     • https://youtube.com/embed/VIDEOID?start=30
     • https://youtu.be/VIDEOID?si=xxxx
     • https://www.youtube-nocookie.com/embed/VIDEOID
   ──────────────────────────────────────────────────────────────────────────── */

const YT_HOST_RE = /(^|\.)youtube(\-nocookie)?\.com$/;

export function isYoutubeUrl(raw: string): boolean {
  try {
    const { hostname, pathname, searchParams } = new URL(raw.trim());

    if (hostname === 'youtu.be') return true;
    if (YT_HOST_RE.test(hostname)) {
      if (pathname.startsWith('/watch') && searchParams.has('v')) return true;
      return pathname.startsWith('/shorts/') || pathname.startsWith('/embed/');
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Extracts a clean 11-character YouTube video ID from any supported URL.
 */
export function getYoutubeVideoId(raw: string): string | null {
  try {
    const url = new URL(raw.trim());

    // Short link: https://youtu.be/VIDEOID?si=xxxx
    if (url.hostname === 'youtu.be') {
      return url.pathname.slice(1).split(/[?/]/)[0] || null;
    }

    // Long links on youtube.com or youtube-nocookie.com
    if (YT_HOST_RE.test(url.hostname)) {
      const { pathname, searchParams } = url;

      if (pathname.startsWith('/watch')) {
        return searchParams.get('v');
      }
      if (pathname.startsWith('/embed/') || pathname.startsWith('/shorts/')) {
        return pathname.split('/')[2]?.split('?')[0] || null;
      }
    }
    return null;
  } catch {
    return null;
  }
}

// Clean URL string
export function cleanUrl(url: string | undefined): string | undefined {
  if (!url || url === 'null' || url === 'undefined') return undefined;
  return url;
}
